import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const listeners = new Set();
let audio = null;
let state = "idle";
let gen = 0; // bumps on every play/stop so stale async requests abort instead of overlapping

const emit = (s) => { state = s; listeners.forEach((fn) => fn(s)); };
export const onSpeechState = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const speechState = () => state;

export const canSpeak = () => typeof window !== "undefined" && ("Audio" in window || "speechSynthesis" in window);

function browserSpeak(text, myGen) {
  if (myGen !== gen) return;
  if (!("speechSynthesis" in window)) { emit("idle"); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.92; u.lang = "en-US";
  u.onend = () => { if (myGen === gen) emit("idle"); };
  u.onerror = () => { if (myGen === gen) emit("idle"); };
  window.speechSynthesis.speak(u);
  emit("speaking");
}

function killAudio() {
  if (!audio) return;
  const a = audio;
  audio = null;
  a.onended = null; a.onerror = null; // detach first so tearing down never triggers the fallback voice
  a.pause();
  a.removeAttribute("src");
  a.load();
}

// Claims the speech slot for a caller that will fetch before playing; returns the token to pass to playUrl.
export function beginSpeech() { stopSpeaking(); emit("loading"); return gen; }

export function stopSpeaking() {
  gen += 1;
  killAudio();
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  emit("idle");
}

const urlCache = new Map();

// Long instructions used to make students wait for one huge synthesis job before any sound.
// Split into sentence-sized chunks so playback starts on the first chunk and the rest prefetch.
const CHUNK_CHARS = 420;
function chunkText(text) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= CHUNK_CHARS) return clean ? [clean] : [];
  const sentences = clean.match(/[^.!?]+[.!?]*\s*/g) || [clean];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + s).length > CHUNK_CHARS && buf) { out.push(buf.trim()); buf = ""; }
    if (s.length > CHUNK_CHARS) {
      for (const piece of s.match(new RegExp(`.{1,${CHUNK_CHARS}}(\\s|$)`, "g")) || [s]) out.push(piece.trim());
    } else buf += s;
  }
  if (buf.trim()) out.push(buf.trim());
  return out.filter(Boolean);
}

async function fetchChunk(part) {
  const hit = urlCache.get(part);
  if (hit) return hit;
  const r = await api.post("/tts", { text: part });
  const url = `${BACKEND}${r.data.url}`;
  urlCache.set(part, url);
  return url;
}

// Plays one chunk and resolves true when it finishes, false if it was superseded or failed.
function playChunk(url, myGen) {
  return new Promise((resolve) => {
    if (myGen !== gen) return resolve(false);
    const a = new Audio();
    audio = a;
    a.crossOrigin = "use-credentials";
    a.src = url;
    a.onended = () => { if (audio === a) audio = null; resolve(true); };
    a.onerror = () => { if (audio === a) audio = null; resolve(false); };
    a.play().then(() => {
      if (myGen !== gen) { killAudio(); return resolve(false); }
      emit("speaking");
    }).catch(() => { if (audio === a) audio = null; resolve(false); });
  });
}

export async function prefetchSpeech(text) {
  const parts = chunkText(text);
  if (!parts.length || urlCache.has(parts[0])) return;
  try { await fetchChunk(parts[0]); } catch { /* fall back at play time */ }
}

const narrationCache = new Map();
export async function getNarration(imageUrl, title, index) {
  if (narrationCache.has(imageUrl)) return narrationCache.get(imageUrl);
  const p = api.post("/slides/narration", { image_url: imageUrl, title, index }).then((r) => ({ text: r.data.text, url: `${BACKEND}${r.data.url}` }));
  narrationCache.set(imageUrl, p);
  p.catch(() => narrationCache.delete(imageUrl));
  return p;
}

// Plays a ready-made audio URL (e.g. slide narration); falls back to reading `fallbackText` in the browser voice.
export async function playUrl(url, fallbackText = "", myGen = null) {
  if (myGen == null) { stopSpeaking(); myGen = gen; }
  if (myGen !== gen) return;
  emit("loading");
  const a = new Audio();
  audio = a;
  a.crossOrigin = "use-credentials";
  a.src = url;
  a.onended = () => { if (audio === a) { audio = null; emit("idle"); } };
  a.onerror = () => { if (audio === a) { audio = null; fallbackText ? browserSpeak(fallbackText, myGen) : emit("idle"); } };
  try {
    await a.play();
    if (myGen !== gen) { if (audio === a) killAudio(); return; }
    emit("speaking");
  } catch {
    if (myGen !== gen || audio !== a) return; // superseded by a newer click — stay silent
    audio = null;
    fallbackText ? browserSpeak(fallbackText, myGen) : emit("idle");
  }
}

// Natural AI narrator, streamed chunk by chunk; falls back to the browser voice if the service is unavailable.
export async function speak(text) {
  if (!text) return;
  stopSpeaking();
  const myGen = gen;
  emit("loading");
  const parts = chunkText(text);
  if (!parts.length) { emit("idle"); return; }
  try {
    let pending = fetchChunk(parts[0]);
    for (let i = 0; i < parts.length; i++) {
      let url = null;
      try { url = await pending; } catch { url = null; }
      if (myGen !== gen) return;
      if (!url) { browserSpeak(parts.slice(i).join(" "), myGen); return; }
      // Start synthesising the next chunk while this one plays.
      pending = i + 1 < parts.length ? fetchChunk(parts[i + 1]).catch(() => null) : null;
      const ok = await playChunk(url, myGen);
      if (myGen !== gen) return;
      if (!ok) { browserSpeak(parts.slice(i).join(" "), myGen); return; }
    }
    if (myGen === gen) emit("idle");
  } catch {
    browserSpeak(text, myGen);
  }
}
